import type {
  CustomOAuthProvider,
  GencowAuthConfig,
  OAuthUserInfo,
  SocialProviderConfig,
} from "@gencow/core";

type ProviderProfile = Record<string, unknown>;

type GenericOAuthConfig = CustomOAuthProvider & {
  providerId: string;
};

type ResolvedSocialAuthConfig = {
  socialProviders: Record<string, SocialProviderConfig>;
  customOAuthConfig: GenericOAuthConfig[];
};

type BuiltInCustomOAuthProvider = Omit<CustomOAuthProvider, "clientId" | "clientSecret">;

function asRecord(value: unknown): ProviderProfile | undefined {
  return value && typeof value === "object" ? (value as ProviderProfile) : undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function hasNativeProviderCredentials(config: SocialProviderConfig): boolean {
  return !!asString(config.clientId) && !!asString(config.clientSecret);
}

function hasCustomProviderCredentials(config: CustomOAuthProvider): boolean {
  return !!asString(config.clientId);
}

function mapKakaoProfileToUser(profile: ProviderProfile): Partial<OAuthUserInfo> {
  const account = asRecord(profile.kakao_account);
  const kakaoProfile = asRecord(account?.profile);

  return {
    id: String(profile.id),
    email: asString(account?.email),
    name: asString(kakaoProfile?.nickname),
    image: asString(kakaoProfile?.profile_image_url),
  };
}

function mapNaverProfileToUser(profile: ProviderProfile): Partial<OAuthUserInfo> {
  const response = asRecord(profile.response);

  return {
    id: asString(response?.id),
    email: asString(response?.email),
    name: asString(response?.name),
    image: asString(response?.profile_image),
  };
}

export const BUILT_IN_CUSTOM_OAUTH_PROVIDERS = {
  kakao: {
    authorizationUrl: "https://kauth.kakao.com/oauth/authorize",
    tokenUrl: "https://kauth.kakao.com/oauth/token",
    userInfoUrl: "https://kapi.kakao.com/v2/user/me",
    scopes: ["profile_nickname", "profile_image", "account_email"],
    mapProfileToUser: mapKakaoProfileToUser,
  },
  naver: {
    authorizationUrl: "https://nid.naver.com/oauth2.0/authorize",
    tokenUrl: "https://nid.naver.com/oauth2.0/token",
    userInfoUrl: "https://openapi.naver.com/v1/nid/me",
    scopes: ["name", "email", "profile_image"],
    mapProfileToUser: mapNaverProfileToUser,
  },
} satisfies Record<string, BuiltInCustomOAuthProvider>;

const NATIVE_SOCIAL_PROVIDER_IDS = new Set(["google", "apple"]);

function toNativeSocialProvider(config: SocialProviderConfig): SocialProviderConfig {
  return {
    clientId: config.clientId,
    clientSecret: config.clientSecret,
    redirectURI: config.redirectURI,
    disableImplicitSignUp: config.disableImplicitSignUp,
    disableSignUp: config.disableSignUp,
  };
}

function toGenericOAuthConfig(providerId: string, config: CustomOAuthProvider): GenericOAuthConfig {
  const defaults =
    BUILT_IN_CUSTOM_OAUTH_PROVIDERS[providerId as keyof typeof BUILT_IN_CUSTOM_OAUTH_PROVIDERS];
  const merged = {
    ...defaults,
    ...config,
  };

  return {
    providerId,
    clientId: merged.clientId,
    clientSecret: merged.clientSecret,
    authorizationUrl: merged.authorizationUrl,
    tokenUrl: merged.tokenUrl,
    userInfoUrl: merged.userInfoUrl,
    scopes: merged.scopes,
    pkce: merged.pkce,
    redirectURI: merged.redirectURI,
    mapProfileToUser: merged.mapProfileToUser,
  };
}

export function resolveSocialAuthConfig(
  config: GencowAuthConfig["socialProviders"] | undefined,
): ResolvedSocialAuthConfig {
  const socialProviders: Record<string, SocialProviderConfig> = {};
  const customOAuthConfig: GenericOAuthConfig[] = [];

  for (const [providerId, providerConfig] of Object.entries(config ?? {})) {
    if (!providerConfig) continue;

    if (NATIVE_SOCIAL_PROVIDER_IDS.has(providerId)) {
      const nativeProviderConfig = providerConfig as SocialProviderConfig;
      if (!hasNativeProviderCredentials(nativeProviderConfig)) continue;
      socialProviders[providerId] = toNativeSocialProvider(nativeProviderConfig);
      continue;
    }

    const customProviderConfig = providerConfig as CustomOAuthProvider;
    if (!hasCustomProviderCredentials(customProviderConfig)) continue;
    customOAuthConfig.push(toGenericOAuthConfig(providerId, customProviderConfig));
  }

  return { socialProviders, customOAuthConfig };
}

export function listEnabledSocialProviderIds(
  config: GencowAuthConfig["socialProviders"] | undefined,
): string[] {
  const { socialProviders, customOAuthConfig } = resolveSocialAuthConfig(config);
  return [...Object.keys(socialProviders), ...customOAuthConfig.map((provider) => provider.providerId)];
}
